/**
 * Stores > HQ Merch Pulse queue at /stores/hq/pulse.
 *
 * Store staff flag a merchandising signal against a SKU: too much on the
 * shelf, send more, a customer requested it, a problem, or something else,
 * with an optional note. This is the only place in Cockpit that shows those
 * signals today; they were deliberately kept off the Allocation grid (that
 * grid was trimmed for browser performance, and a signal per store per SKU
 * there would slow it back down). Modeled on HqQuestions.tsx: newest-first,
 * filterable, with an inline Acknowledge action that clears an open signal.
 *
 * Gated by stores.pulse. 404/503 -> not-available placeholder.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { formatDateTime } from '@/lib/dateUtils';
import type { ApiError } from '@/lib/api';
import {
  useHqPulses,
  useAcknowledgePulse,
  isNotAvailable,
  PULSE_SIGNALS,
  PULSE_SIGNAL_LABELS,
  PULSE_SIGNAL_TONES,
  PULSE_STATUS_TONES,
  type HqPulse as HqPulseRow,
  storeLabel,
} from '@/lib/useStores';
import {
  StoresHeader,
  AccessDeniedCard,
  NotAvailableCard,
  ErrorCard,
  primaryBtnCls,
} from './storesShared';

const selectCls =
  'w-full rounded border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink sm:w-auto sm:min-w-[150px] sm:py-1.5 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';
const filterLabelCls = 'mb-1 block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted';

export function HqPulse() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.pulse', user?.superAdmin);

  const [status, setStatus] = useState('open');
  const [signal, setSignal] = useState('');
  const [storeNo, setStoreNo] = useState('');

  const p = useHqPulses({
    status: status || undefined,
    signal: signal || undefined,
    store_no: storeNo ? Number(storeNo) : undefined,
  });

  // Store options come from the pulses themselves, not from the scorecard's
  // store list: that endpoint demands `stores.scorecard`, so anyone granted
  // ONLY `stores.pulse` would get a 403 and an empty dropdown. Every pulse row
  // already carries store_no and store_name, so the filter can be built from
  // the data on screen with no second permission and no extra request. Built
  // from the unfiltered fetch below so choosing a store never empties the list
  // it was chosen from.
  const allForOptions = useHqPulses({ status: status || undefined });
  const storeOptions = useMemo(() => {
    const byNo = new Map<string, string>();
    for (const row of allForOptions.pulses) {
      if (!byNo.has(row.store_no)) byNo.set(row.store_no, row.store_name ?? '');
    }
    return [...byNo.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      .map(([value, name]) => ({ value, label: storeLabel(value, name) }));
  }, [allForOptions.pulses]);

  if (user && !canView) return <AccessDeniedCard what="The Merch Pulse queue" />;
  if (isNotAvailable(p.error)) {
    return (
      <div>
        <StoresHeader
          title="Merch Pulse"
          actions={<ToolPinToggle toolSlug="stores-hq-pulse" toolName="Merch Pulse" />}
        />
        <NotAvailableCard what="The Merch Pulse queue" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <StoresHeader
        title="Merch Pulse"
        subtitle="Merchandising signals stores flag against a SKU. Acknowledge to clear one off the open list."
        actions={<ToolPinToggle toolSlug="stores-hq-pulse" toolName="Merch Pulse" />}
      />

      <div className="mb-4 grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        <div className="min-w-0">
          <label htmlFor="pulse-status" className={filterLabelCls}>Status</label>
          <select
            id="pulse-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={selectCls}
          >
            <option value="open">Open</option>
            <option value="all">All</option>
          </select>
        </div>
        <div className="min-w-0">
          <label htmlFor="pulse-signal" className={filterLabelCls}>Signal</label>
          <select
            id="pulse-signal"
            value={signal}
            onChange={(e) => setSignal(e.target.value)}
            className={selectCls}
          >
            <option value="">Any signal</option>
            {PULSE_SIGNALS.map((s) => (
              <option key={s} value={s}>{PULSE_SIGNAL_LABELS[s]}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2 min-w-0 sm:col-span-1">
          <label htmlFor="pulse-store" className={filterLabelCls}>Store</label>
          <select
            id="pulse-store"
            value={storeNo}
            onChange={(e) => setStoreNo(e.target.value)}
            className={selectCls}
          >
            <option value="">All stores</option>
            {storeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {p.isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
          ))}
        </div>
      ) : p.error ? (
        <ErrorCard message={p.error.message} />
      ) : p.pulses.length === 0 ? (
        <div className="rounded-lg border border-dashed border-amp-line px-4 py-10 text-center dark:border-amp-dark-line">
          <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">Nothing waiting on you</p>
          <p className="mt-1 text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            This is the good state. When a store flags a merchandising signal, it lands here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {p.pulses.map((pulse) => (
            <PulseCard key={pulse.id} p={pulse} />
          ))}
        </div>
      )}
    </div>
  );
}

function PulseCard({ p }: { p: HqPulseRow }) {
  const isOpen = p.status === 'open';
  const ackM = useAcknowledgePulse();
  const ackErrorStatus = ackM.error ? (ackM.error as ApiError).status : null;
  const showAckError = !!ackM.error && ackErrorStatus !== 409;

  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={PULSE_SIGNAL_TONES[p.signal]}>{PULSE_SIGNAL_LABELS[p.signal]}</Pill>
        <Pill tone={PULSE_STATUS_TONES[p.status]}>{isOpen ? 'Open' : 'Acknowledged'}</Pill>
        <span className="ml-auto text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {formatDateTime(p.created_at)}
        </span>
      </div>

      <p className="mt-2 text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span className="font-medium text-amp-ink dark:text-amp-dark-ink">{p.store_name}</span>
        {` · #${p.store_no}`}
      </p>

      <p className="mt-1 text-[15px] text-amp-ink dark:text-amp-dark-ink">
        <Link
          to={`/commerce/products/${encodeURIComponent(p.item_no)}`}
          className="font-medium text-amp-olive-deep hover:underline dark:text-amp-olive-dark"
        >
          {p.item_no}
        </Link>
        {p.descr ? ` · ${p.descr}` : ''}
      </p>

      {p.note && (
        <p className="mt-2 whitespace-pre-wrap text-[13px] text-amp-ink dark:text-amp-dark-ink">{p.note}</p>
      )}

      <p className="mt-2 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Flagged by {p.created_by_email}
      </p>

      {isOpen ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => ackM.mutate({ id: p.id })}
            disabled={ackM.isPending}
            className={`${primaryBtnCls} min-h-[36px]`}
          >
            {ackM.isPending ? 'Acknowledging...' : 'Acknowledge'}
          </button>
          {showAckError && (
            <p className="mt-2 text-[13px] text-red-700 dark:text-red-300">
              Could not acknowledge: {ackM.error?.message}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-2 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Acknowledged{p.acknowledged_by_email ? ` by ${p.acknowledged_by_email}` : ''}
          {p.acknowledged_at ? ` · ${formatDateTime(p.acknowledged_at)}` : ''}
        </p>
      )}
    </div>
  );
}
